import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Observable, map, shareReplay } from 'rxjs';
import { PDropdownOptions } from 'shared-library';
import {
  DistributionOfClaimsResponse,
  MostCostlyICD10sResponse,
  ClaimReductionComparisonResponse,
  PredictedCostDto,
  StackedBarData,
  FilterOption,
  ApprovalStatusDistributionChart,
  MostFrequentAdmissionICD10,
  TotalNumberOfSubmittedClaims,
  TransitionProbabilitiesAdmissionToDischargeICD10,
  ChartResponses,
  SummaryTables,
  TimeIntervalDto,
  MostFrequentMdc,
  MostCostlyMdcsResponse,
  TableWithChiSquared
} from 'shared-library';
import { environment } from '../../../../environments/environment';
import { TopDoctorsParetoChartResponse } from '../../../../../../shared-library/src/lib/interfaces/reports/top-doctors';
import { TransitionProbabilitiesKenResponse } from '../../../../../../shared-library/src/lib/interfaces/reports/transition-probabilities-ken';

@Injectable({
  providedIn: 'root',
})
export class DashboardChartDataService {
  private http = inject(HttpClient);
  private chartFiltersCache$?: Observable<FilterOption[]>;
  private yearsCache$?: Observable<PDropdownOptions[]>;

  private apiUrl = environment.api;
  readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Cache-Control':
        'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
      Pragma: 'no-cache',
      Expires: '0',
    }),
  };
  private chartFiltersState = signal<FilterOption[]>([]);
  chartFilters = this.chartFiltersState.asReadonly();

  providerOptions = computed(() => {
    const filters = this.chartFilters();
    const providerFilter = filters.find((f) => f.key === 'providers');
    return providerFilter?.dropdownOptions ?? [];
  });

  clinicOptions = computed(() => {
    const filters = this.chartFilters();
    const clinicFilter = filters.find((f) => f.key === 'clinics');
    return clinicFilter?.dropdownOptions ?? [];
  });

  providerGroupsOptions = computed(() => {
    const filters = this.chartFilters();
    const providerGroupsFilter = filters.find(
      (f) => f.key === 'providerGroups'
    );
    return providerGroupsFilter?.dropdownOptions ?? [];
  });

  iCD10CodeTitlesOptions = computed(() => {
    const filters = this.chartFilters();
    const iCD10CodeTitlesFilter = filters.find(
      (f) => f.key === 'iCD10CodeTitles'
    );
    return iCD10CodeTitlesFilter?.dropdownOptions ?? [];
  });

  admissionICD10sOptions = computed(() => {
    const filters = this.chartFilters();
    const admissionICD10sFilter = filters.find(
      (f) => f.key === 'admissionICD10s'
    );
    return admissionICD10sFilter?.dropdownOptions ?? [];
  });

  dischargeICD10sOptions = computed(() => {
    const filters = this.chartFilters();
    const dischargeICD10s = filters.find(
      (f) => f.key === 'dischargeICD10s'
    );
    return dischargeICD10s?.dropdownOptions ?? [];
  });

  claimTypesOptions = computed(() => {
    const filters = this.chartFilters();
    const claimTypesFilter = filters.find((f) => f.key === 'claimTypes');
    return claimTypesFilter?.dropdownOptions ?? [];
  });

  kenGatekeeperCodesOptions = computed(() => {
    const filters = this.chartFilters();
    const kenGatekeeperCodesFilter = filters.find(
      (f) => f.key === 'kenGatekeeperCodes'
    );
    return kenGatekeeperCodesFilter?.dropdownOptions ?? [];
  });

  kenHospitalCodesOptions = computed(() => {
    const filters = this.chartFilters();
    const kenHospitalCodesFilter = filters.find(
      (f) => f.key === 'kenHospitalCodes'
    );
    return kenHospitalCodesFilter?.dropdownOptions ?? [];
  });

  SpecialtyOptions = computed(() => {
    const filters = this.chartFilters();
    const specialtyFilter = filters.find(
      (f) => f.key === 'specialty'
    );
    return specialtyFilter?.dropdownOptions ?? [];
  });

  constructor() { }

  getChartFilters() {
    if (!this.chartFiltersCache$) {
      this.chartFiltersCache$ = this.http
        .get<FilterOption[]>(`${this.apiUrl}Charts/dashboard-filters`)
        .pipe(shareReplay(1));
    }

    this.chartFiltersCache$.subscribe((filters) => {
      this.chartFiltersState.set(filters);
    });

    return this.chartFiltersCache$;
  }

  getDashboardYears(): Observable<PDropdownOptions[]> {
    if (!this.yearsCache$) {
      this.yearsCache$ = this.http
        .get<PDropdownOptions[]>(`${this.apiUrl}Charts/dashboard-years`)
        .pipe(shareReplay(1));
    }

    return this.yearsCache$;
  }

  getDistributionOfClaimsVsHistoricalClaimsChart(
    year: string,
    month: string,
    compareBy: string,
    insuranceResponse: string
  ): Observable<DistributionOfClaimsResponse> {
    const params = new HttpParams()
      .set('year', year)
      .set('month', month)
      .set('compareBy', compareBy)
      .set('insuranceResponse', insuranceResponse);

    return this.http.get<DistributionOfClaimsResponse>(
      `${this.apiUrl}Charts/claims-distribution`,
      { params }
    );
  }

  getMostCostlyICD10s(
    year: string,
    monthName: string,
    compareBy: string,
    groupValue: string,
    topN: number,
    contractType: string,
    quarter: string
  ): Observable<MostCostlyICD10sResponse> {
    const params = new HttpParams()
      .set('year', year)
      .set('month', monthName)
      .set('compareBy', compareBy)
      .set('groupValue', groupValue)
      .set('topN', topN)
      .set('contractType', contractType)
      .set('quarter', quarter);

    return this.http.get<MostCostlyICD10sResponse>(
      `${this.apiUrl}Charts/icd10-most-costly`,
      { params }
    );
  }

  getClaimReductionComparison(
    year: string,
    monthName: string,
    compareBy: string,
    groupValue: string,
    topN: number
  ): Observable<ClaimReductionComparisonResponse> {
    const params = new HttpParams()
      .set('year', year)
      .set('month', monthName)
      .set('compareBy', compareBy)
      .set('groupValue', groupValue)
      .set('topN', topN);

    return this.http.get<ClaimReductionComparisonResponse>(
      `${this.apiUrl}Charts/summary`,
      { params }
    );
  }

  getTotalNumberOfSubmittedClaimsChart(
    year: string,
    month: string,
    compareBy: string,
    groups: string[]
  ) {
    let params = new HttpParams()
      .set('year', year)
      .set('month', month)
      .set('compareBy', compareBy);

    for (const group of groups) {
      params = params.append('groups', group);
    }

    return this.http.get<TotalNumberOfSubmittedClaims>(
      `${this.apiUrl}charts/total-submitted-claims`,
      { params }
    );
  }

  getMostFrequentAdmissionICD10(
    year: string,
    month: string,
    compareBy: string,
    group: string,
    topN: number
  ) {
    const params = new HttpParams()
      .set('year', year)
      .set('month', month)
      .set('compareBy', compareBy)
      .set('group', group)
      .set('topN', topN);

    return this.http.get(
      `${this.apiUrl}charts/most-frequent-admission-icd10`,
      { params, responseType: 'text' }
    )
      .pipe(map((text) => JSON.parse(text) as MostFrequentAdmissionICD10));
  }

  getTransitionProbabilitiesAdmissionToDischargeICD10(
    years: string,
    monthOptions: string,
    compareByOptions: string,
    groups: string,
    admissionICD10s: string,
    dischargeICD10s: string,
    topNOptions: number
  ): Observable<TransitionProbabilitiesAdmissionToDischargeICD10> {
    const params = new HttpParams()
      .set('year', years)
      .set('month', monthOptions)
      .set('compareBy', compareByOptions)
      .set('group', groups)
      .set('admissionICD10', admissionICD10s)
      .set('dischargeICD10', dischargeICD10s)
      .set('topN', topNOptions);

    return this.http.get<TransitionProbabilitiesAdmissionToDischargeICD10>(
      `${this.apiUrl}charts/transition-probabilities-from-admission-to-discharge-icd10`,
      { params }
    );
  }

  getApprovalStatusDistribution(
    years: string,
    month: string,
    compareBy: string,
    group: string,
    TopN: number
  ): Observable<ApprovalStatusDistributionChart> {
    const params = new HttpParams()
      .set('year', years)
      .set('month', month)
      .set('compareBy', compareBy)
      .set('group', group)
      .set('topN', TopN);
    return this.http.get<ApprovalStatusDistributionChart>(
      `${this.apiUrl}charts/approval-status-distribution`,
      { params }
    );
  }

  getPredictedCostChart(
    year: string,
    month: string,
    compareBy: string,
    group: string,
    insuranceResponse: string
  ): Observable<PredictedCostDto> {
    const params = new HttpParams()
      .set('year', year)
      .set('focusMonth', month)
      .set('compareBy', compareBy)
      .set('group', group)
      .set('insuranceResponse', insuranceResponse);

    return this.http.get<PredictedCostDto>(
      `${this.apiUrl}charts/predicted-cost`,
      { params }
    );

  }

  getProportionOfClaimsWithNoCuts(
    year: string,
    clinic: string,
    compareBy: string,
    group: string
  ): Observable<ChartResponses> {
    const params = new HttpParams()
      .set('year', year)
      .set('clinic', clinic)
      .set('compareBy', compareBy)
      .set('group', group);

    return this.http.get<ChartResponses>(
      `${this.apiUrl}charts/no-cuts-proportion`,
      { params }
    );
  }

  getAverageTimeToSettlement(
    year: string,
    month: string,
    clinic: string,
    compareBy: string,
    group: string
  ): Observable<ChartResponses> {
    const params = new HttpParams()
      .set('year', year)
      .set('month', month)
      .set('clinic', clinic)
      .set('compareBy', compareBy)
      .set('group', group);

    return this.http.get<ChartResponses>(
      `${this.apiUrl}charts/average-settlement-time`,
      { params }
    );
  }

  getHospitalizationDuration(
    year: string,
    clinic: string,
    compareBy: string,
    group: string
  ): Observable<ChartResponses> {
    const params = new HttpParams()
      .set('year', year)
      .set('clinic', clinic)
      .set('compareBy', compareBy)
      .set('group', group);

    return this.http.get<ChartResponses>(
      `${this.apiUrl}charts/hospitalization-duration`,
      { params }
    );
  }

  getProportionOfFlaggedClaims(
    year: string,
    clinic: string,
    compareBy: string,
    group: string,
    insuranceResponse: string
  ): Observable<ChartResponses> {
    const params = new HttpParams()
      .set('year', year)
      .set('clinic', clinic)
      .set('compareBy', compareBy)
      .set('group', group)
      .set('insuranceResponse', insuranceResponse);

    return this.http.get<ChartResponses>(
      `${this.apiUrl}charts/flagged-claims-proportion`,
      { params }
    );
  }

  getHighRiskProviderIndex(
    year: string,
    clinic: string,
    compareBy: string,
    group: string,
    insuranceResponse: string
  ): Observable<ChartResponses> {
    const params = new HttpParams()
      .set('year', year)
      .set('clinic', clinic)
      .set('compareBy', compareBy)
      .set('group', group)
      .set('insuranceResponse', insuranceResponse);

    return this.http.get<ChartResponses>(
      `${this.apiUrl}charts/high-risk-provider-index`,
      { params }
    );
  }

  GetTotalClaimedVsApprovedAmount(
    year: string,
    clinic: string,
    compareBy: string,
    group: string,
    insuranceResponse: string
  ): Observable<ChartResponses> {
    const params = new HttpParams()
      .set('year', year)
      .set('clinic', clinic)
      .set('compareBy', compareBy)
      .set('group', group)
      .set('insuranceResponse', insuranceResponse);

    return this.http.get<ChartResponses>(
      `${this.apiUrl}charts/total-claimed-vs-approved`,
      { params }
    );
  }

  getApprovedAmountBasedOnCuts(
    year: string,
    clinic: string,
    compareBy: string,
    group: string,
    insuranceResponse: string
  ): Observable<ChartResponses> {
    const params = new HttpParams()
      .set('year', year)
      .set('clinic', clinic)
      .set('compareBy', compareBy)
      .set('group', group)
      .set('insuranceResponse', insuranceResponse);

    return this.http.get<ChartResponses>(
      `${this.apiUrl}charts/approved-amount-cuts`,
      { params }
    );
  }

  getDiagnosisFlowBetweenAdmissionAndDischarge(
    year: string,
    admissionICD10s: string,
    dischargeICD10s: string
  ): Observable<TimeIntervalDto> {
    const params = new HttpParams()
      .set('year', year)
      .set('admissionICD10', admissionICD10s)
      .set('dischargeICD10', dischargeICD10s)

    return this.http.get<TimeIntervalDto>(
      `${this.apiUrl}charts/diagnosis-flow-admission-discharge`,
      { params }
    );
  }
  getSummaryTables(): Observable<SummaryTables> {
    return this.http.get<SummaryTables>(
      `${this.apiUrl}charts/tables-data`
    );
  }

  getPatientSummary(): Observable<Record<string, string[]>> {
    return this.http.get<Record<string, string[]>>(
      `${this.apiUrl}charts/tables-patient-summary`
    );
  }
  getMeanPredictedCostPerProvider(
    year: string,
    clinic: string,
    compareBy: string,
    group: string,
    columnCost: string
  ): Observable<ChartResponses> {
    const params = new HttpParams()
      .set('year', year)
      .set('clinic', clinic)
      .set('compareBy', compareBy)
      .set('group', group)
      .set('columnCost', columnCost);

    return this.http.get<ChartResponses>(
      `${this.apiUrl}charts/mean-predicted-cost`,
      { params }
    );
  }

  getMeanActualCostPerProvider(
    year: string,
    clinic: string,
    compareBy: string,
    group: string,
    columnCost: string
  ): Observable<ChartResponses> {
    const params = new HttpParams()
      .set('year', year)
      .set('clinic', clinic)
      .set('compareBy', compareBy)
      .set('group', group)
      .set('columnCost', columnCost);

    return this.http.get<ChartResponses>(
      `${this.apiUrl}charts/mean-predicted-cost`,
      { params }
    );
  }

  getProportionOfClaimTypes(
    year: string,
    clinic: string,
    compareBy: string,
    group: string,
    claimType: string
  ): Observable<ChartResponses> {
    const params = new HttpParams()
      .set('year', year)
      .set('clinic', clinic)
      .set('compareBy', compareBy)
      .set('group', group)
      .set('claimType', claimType);

    return this.http.get<ChartResponses>(
      `${this.apiUrl}charts/false-claims-proportion`,
      { params }
    );
  }

  getMedicalExpenses(
    year: string,
    month: string,
    compareBy: string,
    group: string,
    expensesCategory: string,
    kenCategory: string,
    kenCode: string,
    isMissingValues: boolean,
    useGross: boolean,
    useSubExpenses: boolean = false
  ): Observable<StackedBarData> {
    let params = new HttpParams()
      .set('year', year)
      .set('month', month)
      .set('compareBy', compareBy)
      .set('select', group)
      .set('expensesCategory', expensesCategory)
      .set('kenCategory', kenCategory)
      .set('kenCode', kenCode)
      .set('hasMissingValues', isMissingValues)
      .set('useGross', useGross)
      .set('useSubExpenses', useSubExpenses)

    return this.http.get<StackedBarData>(
      `${this.apiUrl}charts/medical-expenses`,
      { params }
    );
  }

  getMostFrequentMdc(
    year: string,
    month: string,
    compareBy: string,
    group: string,
    topN: number,
    contractType: string,
    quarter: string
  ) {
    return this.http
      .get(
        `${this.apiUrl}charts/most-frequent-mdc?year=${year}&month=${month}&compareBy=${compareBy}&group=${group}&topN=${topN}&contractType=${contractType}&quarter=${quarter}`,
        { responseType: 'text' }
      )
      .pipe(map((text) => JSON.parse(text) as MostFrequentMdc));
  }

  getMostCostlyMdcs(
    year: string,
    monthName: string,
    compareBy: string,
    groupValue: string,
    topN: number,
    contractType: string,
    quarter: string
  ): Observable<MostCostlyMdcsResponse> {
    const params = new HttpParams()
      .set('year', year)
      .set('month', monthName)
      .set('compareBy', compareBy)
      .set('groupValue', groupValue)
      .set('topN', topN)
      .set('contractType', contractType)
      .set('quarter', quarter);

    return this.http.get<MostCostlyMdcsResponse>(
      `${this.apiUrl}Charts/mdc-most-costly`,
      { params }
    );
  }

  getMissingDRGs(
    year: string,
    month: string,
    compareBy: string,
  ): Observable<TableWithChiSquared> {
    const params = new HttpParams()
      .set('year', year)
      .set('month', month)
      .set('groupBy', compareBy)

    

    return this.http.get<TableWithChiSquared>(
      `${this.apiUrl}Charts/drgs-missing`,
      { params }
    );
  }

  getTopDRGs(
    year: string,
    month: string,
    compareBy: string
  ): Observable<TableWithChiSquared> {
    const params = new HttpParams()
      .set('year', year)
      .set('month', month)
      .set('groupBy', compareBy)

    

    return this.http.get<TableWithChiSquared>(
      `${this.apiUrl}Charts/drgs-top`,
      { params }
    );
  }

  getTopDoctors(
    year: string,
    month: string,
    filterValue: string,
    filterField: string = '0', // 0 for Specialty, 1 for Clinic
    topN: number
  ) {
    const params = new HttpParams()
      .set('year', year)
      .set('month', month)
      .set('filterValue', filterValue)
      .set('filterField', filterField)
      .set('topN', topN);

    return this.http.get(
      `${this.apiUrl}charts/top-doctors-pareto`,
      { params, responseType: 'text' }
    )
      .pipe(map((text) => JSON.parse(text) as TopDoctorsParetoChartResponse));
  }

  getTransitionProbabilitiesKen(
    years: string,
    monthOptions: string,
    compareByOptions: string,
    groups: string,
    hospitalDrg: string,
    gatekeeperDrg: string,
    topNOptions: number
  ): Observable<TransitionProbabilitiesKenResponse> {
    const params = new HttpParams()
      .set('year', years)
      .set('month', monthOptions)
      .set('compareBy', compareByOptions)
      .set('group', groups)
      .set('selectedAdmKen', hospitalDrg)
      .set('selectedDisKen', gatekeeperDrg)
      .set('topN', topNOptions);

    return this.http.get<TransitionProbabilitiesKenResponse>(
      `${this.apiUrl}charts/transition-probabilities-ken`,
      { params }
    );
  }

  getDrgFinancialSummary(
    year: string,
    month: string,
    compareBy: string,
    group: string,
    drgCode: string,
  ): Observable<TableWithChiSquared> {
    const params = new HttpParams()
      .set('year', year)
      .set('month', month)
      .set('groupBy', compareBy)
      .set('group', group)
      .set('drgCode', drgCode)
    
    return this.http.get<TableWithChiSquared>(
      `${this.apiUrl}Charts/drgs-financial-summary`,
      { params }
    );
  }

  getDrgFinancialSummaryByClinic(
    year: string,
    month: string,
    compareBy: string,
    group: string,
    drgCode: string,
  ): Observable<TableWithChiSquared> {
    const params = new HttpParams()
      .set('year', year)
      .set('month', month)
      .set('groupBy', compareBy)
      .set('group', group)
      .set('drgCode', drgCode)

    

    return this.http.get<TableWithChiSquared>(
      `${this.apiUrl}Charts/drgs-financial-summary-clinic`,
      { params }
    );
  }

}
